import {
  CLASSIC_ECHOES,
  LEVEL_ECHO_TABLE,
  CONFIG,
  CONFIG_VERSION,
  ENGINE_VERSION,
  STEP,
  practiceEchoes,
  trainingEchoes,
  type EchoDef,
} from "./config";
import type { CollisionRecord, EngineEvent, PickRecord } from "./events";
import { PositionHistory, type Vec2 } from "./history";
import { resolveWalls, type EntityKind, type LevelDef, type Wall } from "./levels";
import { placeShard } from "./spawner";
import { SplitMix64 } from "./splitmix64";

/** A spawned, not-yet-consumed level entity (Worlds §W5). */
export interface ActiveEntity {
  kind: EntityKind;
  x: number;
  y: number;
  spawnStep: number;
}

export type RunKind = "classic" | "daily" | "practice" | "level";

export interface PracticeConfig {
  A: boolean;
  B: boolean;
  C: boolean;
  D: boolean;
  collision: boolean;
  infinite: boolean;
}

export interface RunInit {
  kind: RunKind;
  seed: bigint;
  /** Unranked onboarding preset — classic only (spec §7.1). */
  training?: boolean;
  practice?: PracticeConfig;
  /** Required when kind === "level" (Worlds §W1). */
  level?: LevelDef;
  /** Pre-run power-up charge carried into a level (Worlds §W8). */
  loadout?: EntityKind;
}

export type RunOutcome = "cleared" | "caught" | "abandoned";

export interface RunRecord {
  schemaVersion: number;
  engineVersion: number;
  configVersion: number;
  kind: RunKind;
  seed: string;
  duration: number;
  outcome: RunOutcome;
  score: number;
  shardScore: number;
  completionBonus: number;
  shardsCollected: number;
  longestCombo: number;
  remainingLives: number;
  collisions: CollisionRecord[];
  picks: PickRecord[];
  route: { t: number; x: number; y: number }[];
  wasTraining: boolean;
  wasAssisted: boolean;
  dateISO: string;
  /** Level runs only (Worlds §W1). */
  levelId?: string;
  quota?: number;
  stars?: number;
  /** Reflex Rating v1 (Worlds §W6). */
  reflex?: number;
}

/**
 * EchoEngine — pure deterministic mechanics (spec §19).
 *
 * All timing thresholds compare integer step counts, so "exactly 2.10 s"
 * and "unlocks at exactly the configured time" are step-exact in both this
 * engine and the Swift mirror. Given the same seed and input sequence the
 * two engines produce identical positions, spawns, scores, and outcomes.
 *
 * The engine never reads the wall clock, device refresh rate, or renderer
 * state. The driver owns the accumulator, the 0.075 s hit freeze (real time,
 * no sim steps consumed), and the 12-steps-per-frame debt cap (spec §19.1).
 */
export class EchoEngine {
  readonly kind: RunKind;
  readonly seed: bigint;
  readonly training: boolean;
  readonly practice: PracticeConfig | null;
  readonly level: LevelDef | null;
  readonly walls: readonly Wall[];
  readonly defs: readonly EchoDef[];
  readonly history = new PositionHistory();

  /**
   * The clock starts on first input, never on scene presentation (Worlds
   * §W2 — a player who hasn't touched the screen cannot lose).
   */
  started = false;

  private readonly rng: SplitMix64;

  /** Completed mechanics steps. t = stepIndex × 1/120. */
  stepIndex = 0;
  t = 0;

  px: number = CONFIG.playerStartX;
  py: number = CONFIG.playerStartY;
  tx: number = CONFIG.playerStartX;
  ty: number = CONFIG.playerStartY;
  touching = false;
  /** Debug/desktop keyboard vector (unit length or zero); 205 u/s (spec §4.2). */
  keyDirX = 0;
  keyDirY = 0;

  lives: number = CONFIG.lives;
  invulnUntilStep = -1;
  score = 0;
  shardScore = 0;
  completionBonus = 0;
  combo = 1;
  comboMax = 1;
  lastPickStep = -1_000_000;
  shardsCollected = 0;

  shard: Vec2;
  prevShard: Vec2 | null = null;

  picks: PickRecord[] = [];
  hits: CollisionRecord[] = [];
  edgeSteps = 0;
  echoAliveSteps = 0;

  // Entities + effects (Worlds §W5)
  activeEntities: ActiveEntity[] = [];
  private entityCursor = 0;
  /** Shield absorbs one hit while stepIndex < shieldUntilStep. */
  shieldUntilStep = -1;
  /** Echo playback frozen while stepIndex <= stasisUntilStep. */
  stasisUntilStep = -1;
  private totalFrozenSteps = 0;
  /** Steps stolen from the clock by TIMELEAK pickups. */
  stolenSteps = 0;
  /** GLITCH (Worlds §W8): drag target point-reflected through the spark. */
  glitchUntilStep = -1;
  /** ECHO BRAKE (v5): echoes replay at half speed while stepIndex < this. */
  brakeUntilStep = -1;
  /** SHOCKWAVE (v5): radial blast state — fire step + per-echo unit dirs. */
  private shockStep = -1_000_000;
  private shockDirs = new Map<string, Vec2>();
  /** Pre-run loadout charge (Worlds §W8); campaign only, fired by the player. */
  loadoutCharge: EntityKind | null = null;
  /** Second Wind used — the record is marked assisted (spec §13.2 fairness). */
  assisted = false;

  // Reflex telemetry (Worlds §W6)
  private pressureSteps = 0;
  private pressureDistSum = 0;
  private pressureSpeedSum = 0;

  over = false;
  win = false;
  startedAtISO = new Date().toISOString();

  constructor(init: RunInit) {
    this.kind = init.kind;
    this.seed = init.seed;
    this.training = init.kind === "classic" && !!init.training;
    this.practice = init.kind === "practice" ? (init.practice ?? null) : null;
    this.level = init.kind === "level" ? (init.level ?? null) : null;
    this.loadoutCharge = init.kind === "level" ? (init.loadout ?? null) : null;
    this.walls = this.level?.walls ?? [];
    this.rng = new SplitMix64(init.seed);

    if (this.level) {
      this.defs = levelEchoes(this.level);
    } else if (this.kind === "practice" && this.practice) {
      this.defs = practiceEchoes(this.practice);
    } else if (this.training) {
      this.defs = trainingEchoes();
    } else {
      this.defs = CLASSIC_ECHOES;
    }

    // The first history sample is the start position at time 0 (spec §5.4).
    this.history.push(this.px, this.py);
    this.shard = this.spawnShard();
  }

  /** Latest input state; applies to every subsequent step until changed. */
  setInput(tx: number, ty: number, touching: boolean): void {
    this.tx = Math.max(CONFIG.boundsMinX, Math.min(CONFIG.boundsMaxX, tx));
    this.ty = Math.max(CONFIG.boundsMinY, Math.min(CONFIG.boundsMaxY, ty));
    this.touching = touching;
    if (touching) this.started = true;
  }

  release(): void {
    this.touching = false;
  }

  setKeyDirection(dx: number, dy: number): void {
    this.keyDirX = dx;
    this.keyDirY = dy;
    if (dx !== 0 || dy !== 0) this.started = true;
  }

  get infinite(): boolean {
    return this.kind === "practice" && !!this.practice?.infinite;
  }

  private get collisionEnabled(): boolean {
    return this.kind !== "practice" || !!this.practice?.collision;
  }

  echoActive(def: EchoDef): boolean {
    return this.stepIndex >= def.unlockStep;
  }

  echoCollidable(def: EchoDef): boolean {
    return this.stepIndex >= def.unlockStep + CONFIG.spawnFadeSteps;
  }

  /**
   * Echo playback time `stepsAhead` steps from now (Worlds §W5): stasis lags
   * playback behind the wall clock by every frozen step, so echoes resume from
   * where they stopped. Future frozen steps inside a live stasis window are
   * knowable exactly — the spawner's route prediction stays deterministic.
   */
  echoTimeAt(stepsAhead: number): number {
    const futureFrozen = Math.max(
      0,
      Math.min(this.stepIndex + stepsAhead, this.stasisUntilStep) - this.stepIndex,
    );
    return (this.stepIndex + stepsAhead - this.totalFrozenSteps - futureFrozen) * STEP;
  }

  /** Reflect a replayed sample for MIRROR echoes (Worlds §W8). */
  private mirrored(def: EchoDef, p: Vec2 | null): Vec2 | null {
    if (!p || !def.mirror) return p;
    return { x: CONFIG.boundsMinX + CONFIG.boundsMaxX - p.x, y: p.y };
  }

  /**
   * SHOCKWAVE displacement (v5): a decaying radial offset away from the blast
   * origin, clamped to bounds. Applied to render AND collision positions; the
   * shard spawner intentionally ignores it (mirrored in Swift).
   */
  private shocked(def: EchoDef, p: Vec2 | null): Vec2 | null {
    if (!p || this.shockStep < 0) return p;
    const age = this.stepIndex - this.shockStep;
    if (age >= CONFIG.shockDecaySteps) return p;
    const dir = this.shockDirs.get(def.id);
    if (!dir) return p;
    const k = CONFIG.shockPushDistance * (1 - age / CONFIG.shockDecaySteps);
    return {
      x: Math.max(CONFIG.boundsMinX, Math.min(CONFIG.boundsMaxX, p.x + dir.x * k)),
      y: Math.max(CONFIG.boundsMinY, Math.min(CONFIG.boundsMaxY, p.y + dir.y * k)),
    };
  }

  echoPosition(def: EchoDef): Vec2 | null {
    if (!this.echoActive(def)) return null;
    return this.shocked(def, this.mirrored(def, this.history.positionAt(this.echoTimeAt(0) - def.delay)));
  }

  /** Echo trail sample for rendering: k × 0.045 s behind the head. */
  echoTrailPoint(def: EchoDef, k: number): Vec2 | null {
    return this.shocked(def, this.mirrored(def, this.history.positionAt(this.echoTimeAt(0) - def.delay - k * CONFIG.trailSpacing)));
  }

  /** Effective round length after TIMELEAK theft (Worlds §W5). */
  get effectiveRoundSteps(): number {
    return CONFIG.roundSteps - this.stolenSteps;
  }

  get shieldActive(): boolean {
    return this.stepIndex < this.shieldUntilStep;
  }

  get stasisActive(): boolean {
    return this.stepIndex <= this.stasisUntilStep;
  }

  private spawnShard(): Vec2 {
    return placeShard({
      rng: this.rng,
      history: this.history,
      echoTimeAt: (stepsAhead) => this.echoTimeAt(stepsAhead),
      stepIndex: this.stepIndex,
      playerX: this.px,
      playerY: this.py,
      prevShard: this.prevShard,
      activeEchoes: this.defs.filter((d) => this.echoActive(d)),
      walls: this.walls,
    });
  }

  /** Advance exactly one 1/120 s mechanics step (spec §19.2 order). */
  step(): EngineEvent[] {
    if (this.over || !this.started) return [];
    const C = CONFIG;
    const events: EngineEvent[] = [];

    // 2. Advance simulation time. Steps inside a stasis window accrue frozen
    // time — echo playback lags by exactly that many steps (Worlds §W5).
    this.stepIndex++;
    this.t = this.stepIndex * STEP;
    if (this.stepIndex <= this.stasisUntilStep) this.totalFrozenSteps++;
    else if (this.stepIndex < this.brakeUntilStep && this.stepIndex % 2 === 0) this.totalFrozenSteps++;
    const preStepX = this.px;
    const preStepY = this.py;

    // 3. Update player position toward target (or debug keyboard vector).
    // GLITCH inverts the drag: the target is point-reflected through the
    // spark (Worlds §W8).
    const speed = C.touchSpeed;
    if (this.touching) {
      let gx = this.tx;
      let gy = this.ty;
      if (this.stepIndex < this.glitchUntilStep) {
        gx = Math.max(C.boundsMinX, Math.min(C.boundsMaxX, 2 * this.px - this.tx));
        gy = Math.max(C.boundsMinY, Math.min(C.boundsMaxY, 2 * this.py - this.ty));
      }
      const dx = gx - this.px;
      const dy = gy - this.py;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 1e-9) {
        const move = Math.min(dist, speed * STEP);
        this.px += (dx / dist) * move;
        this.py += (dy / dist) * move;
      }
    } else if (this.keyDirX !== 0 || this.keyDirY !== 0) {
      this.px += this.keyDirX * C.debugKeySpeed * STEP;
      this.py += this.keyDirY * C.debugKeySpeed * STEP;
    }

    // 4. Clamp to arena bounds.
    this.px = Math.max(C.boundsMinX, Math.min(C.boundsMaxX, this.px));
    this.py = Math.max(C.boundsMinY, Math.min(C.boundsMaxY, this.py));

    // 4b. Resolve wall penetration, then re-clamp (Worlds §W1). Push-out plus
    // continued target-seeking produces natural sliding along wall faces.
    // Gates evaluate at the current step (§W4) — a moving slab pushes you.
    if (this.walls.length > 0) {
      const resolved = resolveWalls(this.px, this.py, this.walls, C.playerCollisionRadius, this.stepIndex);
      this.px = Math.max(C.boundsMinX, Math.min(C.boundsMaxX, resolved.x));
      this.py = Math.max(C.boundsMinY, Math.min(C.boundsMaxY, resolved.y));
    }

    const edgeDist = Math.min(
      this.px - C.boundsMinX,
      C.boundsMaxX - this.px,
      this.py - C.boundsMinY,
      C.boundsMaxY - this.py,
    );
    if (edgeDist < C.edgeZone) this.edgeSteps++;

    // 5. Write history sample on each 60 Hz boundary.
    if (this.stepIndex % 2 === 0) {
      this.history.push(this.px, this.py);
    }

    // 6. Resolve shard pickup and spawn replacement in the same step.
    {
      const dx = this.px - this.shard.x;
      const dy = this.py - this.shard.y;
      if (dx * dx + dy * dy < C.pickupDistanceSq) {
        const inWindow = this.stepIndex - this.lastPickStep <= C.comboWindowSteps;
        this.combo = inWindow ? Math.min(this.combo + 1, C.comboMax) : 1;
        if (this.combo > this.comboMax) this.comboMax = this.combo;
        this.lastPickStep = this.stepIndex;
        const points = C.baseShardPoints * this.combo;
        if (this.kind !== "practice") {
          this.shardScore += points;
          this.score += points;
        }
        if (this.level && this.shardsCollected + 1 === this.level.quota) {
          events.push({ type: "quotaMet", quota: this.level.quota });
        }
        this.shardsCollected++;
        this.picks.push({ x: this.shard.x, y: this.shard.y, combo: this.combo, t: this.t });
        events.push({ type: "pickup", x: this.shard.x, y: this.shard.y, combo: this.combo, points });
        this.prevShard = this.shard;
        this.shard = this.spawnShard();
      }
    }

    // 6b. Entity spawns + pickups + armed-mine contact (Worlds §W5).
    if (this.level?.entities) {
      const sched = this.level.entities;
      while (this.entityCursor < sched.length && sched[this.entityCursor].atStep <= this.stepIndex) {
        const s = sched[this.entityCursor];
        this.entityCursor++;
        this.activeEntities.push({ kind: s.kind, x: s.x, y: s.y, spawnStep: this.stepIndex });
        events.push({ type: "entitySpawn", kind: s.kind, x: s.x, y: s.y });
      }
    }
    if (this.activeEntities.length > 0) {
      let write = 0;
      for (let i = 0; i < this.activeEntities.length; i++) {
        const e = this.activeEntities[i];
        const dx = this.px - e.x;
        const dy = this.py - e.y;
        const dSq = dx * dx + dy * dy;
        let consumed = false;
        if (e.kind === "mine") {
          const armed = this.stepIndex >= e.spawnStep + C.mineTelegraphSteps;
          const reach = C.mineRadius + C.playerCollisionRadius;
          if (armed && this.stepIndex >= this.invulnUntilStep && dSq < reach * reach) {
            consumed = true;
            if (this.shieldActive) {
              // v5: the shield survives — every hit inside the window absorbs.
              this.invulnUntilStep = this.stepIndex + C.invulnSteps;
              events.push({ type: "shieldAbsorb", x: e.x, y: e.y });
            } else {
              this.lives--;
              this.combo = 1;
              this.invulnUntilStep = this.stepIndex + C.invulnSteps;
              events.push({ type: "mineHit", x: e.x, y: e.y, livesLeft: this.lives });
              if (this.lives <= 0) {
                this.activeEntities.length = write;
                this.finish(false, events);
                return events;
              }
            }
          }
        } else if (dSq < C.entityPickupDistanceSq) {
          consumed = true;
          events.push({ type: "entityPickup", kind: e.kind, x: e.x, y: e.y });
          this.applyEntityEffect(e.kind);
        }
        if (!consumed) {
          this.activeEntities[write] = e;
          write++;
        }
      }
      this.activeEntities.length = write;
    }

    // 7. Timer completion resolves before echo collision (spec §4.6).
    // Level runs clear only if the shard quota was met (Worlds §W1); TIMELEAK
    // theft shortens the effective round (§W5).
    if (!this.infinite && this.stepIndex >= this.effectiveRoundSteps) {
      this.finish(this.level ? this.shardsCollected >= this.level.quota : true, events);
      return events;
    }

    // 8. Echo activation (unlock + telegraph events are step-exact).
    for (const def of this.defs) {
      if (this.stepIndex === def.unlockStep) {
        events.push({ type: "unlock", echoId: def.id });
      } else if (this.stepIndex < def.unlockStep) {
        const remSteps = def.unlockStep - this.stepIndex;
        if (remSteps === 240 || remSteps === 120 || remSteps === 30) {
          events.push({ type: "warnTick", echoId: def.id, mark: remSteps / 120 });
        }
      }
    }
    if (this.defs.length > 0 && this.stepIndex >= this.defs[0].unlockStep) {
      this.echoAliveSteps++;
    }

    // 9./10. Invulnerability + at most one echo collision per step. Echo
    // positions use effective (stasis-aware) playback time; the reflex
    // telemetry reuses the same distance pass (Worlds §W5/§W6).
    {
      const echoNow = this.echoTimeAt(0);
      let minDistSq = Infinity;
      let collided: { def: EchoDef; ep: Vec2 } | null = null;
      for (const def of this.defs) {
        if (!this.echoCollidable(def)) continue;
        const ep = this.shocked(def, this.mirrored(def, this.history.positionAt(echoNow - def.delay)));
        if (!ep) continue;
        const dx = this.px - ep.x;
        const dy = this.py - ep.y;
        const dSq = dx * dx + dy * dy;
        if (dSq < minDistSq) minDistSq = dSq;
        const threshold = def.collisionThreshold;
        if (!collided && dSq < threshold * threshold) {
          collided = { def, ep };
        }
      }
      if (minDistSq < C.reflexPressureRangeSq) {
        const mdx = this.px - preStepX;
        const mdy = this.py - preStepY;
        this.pressureSteps++;
        this.pressureDistSum += Math.sqrt(minDistSq);
        this.pressureSpeedSum += Math.sqrt(mdx * mdx + mdy * mdy);
      }
      if (collided && this.collisionEnabled && this.stepIndex >= this.invulnUntilStep) {
        const { def, ep } = collided;
        const hx = (this.px + ep.x) / 2;
        const hy = (this.py + ep.y) / 2;
        if (this.shieldActive) {
          // Phase Shield absorbs the hit whole: no life, no combo break (§W5).
          // v5: the shield survives — unlimited absorbs inside its window.
          this.invulnUntilStep = this.stepIndex + C.invulnSteps;
          events.push({ type: "shieldAbsorb", x: hx, y: hy });
        } else {
          this.lives--;
          this.combo = 1;
          this.invulnUntilStep = this.stepIndex + C.invulnSteps;
          this.hits.push({ echoId: def.id, delay: def.delay, x: hx, y: hy, t: this.t });
          events.push({
            type: "hit",
            echoId: def.id,
            delay: def.delay,
            x: hx,
            y: hy,
            livesLeft: this.lives,
          });
          if (this.lives <= 0) {
            this.finish(false, events);
            return events;
          }
        }
      }
    }

    // 11. Combo timeout (eager reset drives the HUD chip and audio cue).
    if (this.combo > 1 && this.stepIndex - this.lastPickStep > C.comboWindowSteps) {
      this.combo = 1;
      events.push({ type: "comboTimeout" });
    }

    // 12. Final-six-seconds percussive pulse on whole-second boundaries.
    if (!this.infinite && this.stepIndex % 120 === 0) {
      const remaining = (this.effectiveRoundSteps - this.stepIndex) / 120;
      if (remaining > 0 && remaining <= C.finalWarningSeconds) {
        events.push({ type: "secondPulse", remaining });
      }
    }

    return events;
  }

  private finish(win: boolean, events: EngineEvent[]): void {
    this.over = true;
    this.win = win;
    this.touching = false;
    this.completionBonus = win ? CONFIG.completionBonusPerLife * this.lives : 0;
    if (this.kind !== "practice") this.score += this.completionBonus;
    events.push({ type: "ended", win });
  }

  /**
   * Star contract (Worlds §W1): 1★ = quota, 2★ = star2 shards, 3★ = star3
   * shards with every life intact. Thresholds are precomputed integers in the
   * level definition so both engines compare, never round.
   */
  get stars(): number {
    if (!this.level || !this.over || !this.win) return 0;
    if (this.shardsCollected >= this.level.star3 && this.lives === CONFIG.lives) return 3;
    if (this.shardsCollected >= this.level.star2) return 2;
    return 1;
  }

  /**
   * Reflex Rating v1 (Worlds §W6), 0–1000. Pressure telemetry only — no new
   * per-step storage: survival, near-miss tightness (skimming your past pays),
   * sustained speed under pressure, and time spent under pressure at all.
   * Positive-half rounding is identical in JS and Swift.
   */
  get reflex(): number {
    const C = CONFIG;
    const effRound = this.effectiveRoundSteps;
    const surviveScore = this.win ? 1 : Math.min(1, this.stepIndex / effRound);
    const avgClear = this.pressureSteps > 0 ? this.pressureDistSum / this.pressureSteps : C.reflexPressureRange;
    const clearanceScore = Math.max(
      0,
      Math.min(1, (C.reflexPressureRange - avgClear) / (C.reflexPressureRange - C.collisionThreshold)),
    );
    const avgSpeed = this.pressureSteps > 0 ? this.pressureSpeedSum / this.pressureSteps / STEP : 0;
    const speedScore = Math.max(0, Math.min(1, avgSpeed / C.touchSpeed));
    const pressureFrac = Math.max(0, Math.min(1, this.pressureSteps / 1200));
    return Math.round(
      1000 * (0.3 * surviveScore + 0.3 * clearanceScore + 0.25 * speedScore + 0.15 * pressureFrac),
    );
  }

  /** Apply a power-up/-down effect at the current step (Worlds §W5/§W8). */
  private applyEntityEffect(kind: EntityKind): void {
    const C = CONFIG;
    if (kind === "timeleak") {
      this.stolenSteps += C.timeLeakSteps;
    } else if (kind === "shield") {
      this.shieldUntilStep = this.stepIndex + C.shieldSteps;
    } else if (kind === "stasis") {
      this.stasisUntilStep = this.stepIndex + C.stasisSteps;
    } else if (kind === "glitch") {
      this.glitchUntilStep = this.stepIndex + C.glitchSteps;
    } else if (kind === "surge") {
      // Echoes surge toward the present: frozen lag is repaid (never past now).
      this.totalFrozenSteps = Math.max(0, this.totalFrozenSteps - C.surgeSteps);
    } else if (kind === "shockwave") {
      // v5: radial blast — every active echo is shoved directly away from the
      // spark, then glides back onto its timeline over shockDecaySteps.
      // Fallback directions are exact literals (no transcendentals) so both
      // engines agree to the last bit.
      this.shockStep = this.stepIndex;
      this.shockDirs.clear();
      const fallback: Vec2[] = [
        { x: 0, y: -1 }, { x: 1, y: 0 }, { x: 0, y: 1 }, { x: -1, y: 0 },
        { x: 0.7071067811865476, y: -0.7071067811865476 },
        { x: -0.7071067811865476, y: 0.7071067811865476 },
      ];
      const now = this.echoTimeAt(0);
      for (let i = 0; i < this.defs.length; i++) {
        const def = this.defs[i];
        if (!this.echoActive(def)) continue;
        const ep = this.mirrored(def, this.history.positionAt(now - def.delay));
        if (!ep) continue;
        let dx = ep.x - this.px;
        let dy = ep.y - this.py;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 1e-6) {
          const f = fallback[i % fallback.length];
          dx = f.x;
          dy = f.y;
        } else {
          dx /= len;
          dy /= len;
        }
        this.shockDirs.set(def.id, { x: dx, y: dy });
      }
    } else if (kind === "overclock") {
      // v5: ECHO BRAKE — echoes replay at half speed for brakeSteps.
      this.brakeUntilStep = this.stepIndex + C.brakeSteps;
    }
  }

  /**
   * Fire the pre-run loadout charge (Worlds §W8). Player-triggered input,
   * campaign only; the charge is consumed whether or not it was well timed.
   */
  useLoadout(): boolean {
    if (this.over || !this.started || this.loadoutCharge === null) return false;
    const kind = this.loadoutCharge;
    this.loadoutCharge = null;
    this.applyEntityEffect(kind);
    return true;
  }

  /**
   * Second Wind (Worlds §W3): revive a lost level run with one life. The run
   * is permanently marked assisted — assisted records never reach
   * leaderboards or bests (spec fairness rules), only campaign stars.
   */
  revive(): boolean {
    if (!this.over || this.win || this.lives > 0 || this.kind !== "level") return false;
    this.over = false;
    this.assisted = true;
    this.lives = 1;
    this.invulnUntilStep = this.stepIndex + CONFIG.invulnSteps * 2;
    return true;
  }

  /** Immutable summary for persistence + Traceprint generation (spec §19.3). */
  buildRunRecord(outcomeOverride?: RunOutcome): RunRecord {
    const route: { t: number; x: number; y: number }[] = [];
    const first = Math.max(0, this.history.count - CONFIG.historyCapacity);
    for (let i = first; i < this.history.count; i++) {
      const s = this.history.sampleAt(i);
      if (s) route.push({ t: i / 60, x: s.x, y: s.y });
    }
    return {
      schemaVersion: 1,
      engineVersion: ENGINE_VERSION,
      configVersion: CONFIG_VERSION,
      kind: this.kind,
      seed: this.seed.toString(),
      duration: Math.min(this.t, this.effectiveRoundSteps / 120),
      outcome: outcomeOverride ?? (this.win ? "cleared" : "caught"),
      score: this.score,
      shardScore: this.shardScore,
      completionBonus: this.completionBonus,
      shardsCollected: this.shardsCollected,
      longestCombo: this.comboMax,
      remainingLives: this.lives,
      collisions: this.hits,
      picks: this.picks,
      route,
      wasTraining: this.training,
      wasAssisted: this.assisted,
      dateISO: this.startedAtISO,
      levelId: this.level?.id,
      quota: this.level?.quota,
      stars: this.level ? this.stars : undefined,
      reflex: this.reflex,
    };
  }
}

/**
 * Level echo roster: ids reference the classic schedule's shape/notch/collision
 * identity; the level overrides only the unlock step (Worlds §W1).
 */
function levelEchoes(level: LevelDef): EchoDef[] {
  const out: EchoDef[] = [];
  for (const e of level.echoes) {
    const base = LEVEL_ECHO_TABLE.find((d) => d.id === e.id);
    if (!base) continue;
    out.push({ ...base, unlockStep: e.unlockStep });
  }
  return out;
}
